// src/api/todo.ts — Daily Planner API client
import api from "./api";

export type TodoStatus = "PENDING" | "IN_PROGRESS" | "COMPLETED" | "MISSED";
export type TodoPriority = "LOW" | "MEDIUM" | "HIGH" | "URGENT";

// Personal todo item (lives in `todos` table)
export interface PersonalTodo {
  id: string;
  source: "personal";
  title: string;
  description?: string | null;
  notes?: string | null;
  priority: TodoPriority;
  status: TodoStatus;
  due_date: string | null;       // YYYY-MM-DD
  time_of_day: string | null;
  reminder_at: string | null;
  is_recurring: boolean;
  recurrence_rule: any;
  completed_at: string | null;
  created_at: string;
  source_task_id?: string | null;
  source_checklist_item_id?: string | null;
  // Populated when the todo was assigned by someone else via /todo/assign.
  // Null for self-created todos (the legacy /todo/quick path).
  created_by?: string | null;
  created_by_name?: string | null;
  // Optional client / vendor / project links (nullable). Names are resolved
  // client-side from the lists the planner loads for the tag dropdowns.
  client_id?: string | null;
  vendor_id?: string | null;
  project_id?: string | null;
}

// Assigned task surfaced in the planner (read-only view of `tasks`)
export type TaskStatus =
  | "TODO" | "IN_PROGRESS" | "IN_REVIEW" | "BLOCKED" | "DONE" | "CANCELLED";

export type TaskRoleInTask = "PREPAROR" | "OWNER" | "COLLABORATOR" | "REVIEWER";

export interface AssignedItem {
  id: string;                    // Task.id
  source: "assigned";
  title: string;
  description?: string | null;
  status: TaskStatus;
  priority: TodoPriority;
  due_date: string | null;
  completed_at: string | null;
  progress_percent: number;
  is_overdue: boolean;
  tags: string[];
  task_type: string;
  created_by: string | null;
  // Display name for created_by — resolved on the backend so the planner
  // card can render "by {name}" without a per-row lookup.
  created_by_name?: string | null;
  primary_assignee_id: string | null;
  link_id?: string | null;       // present when pinned to today
  // The caller's active role(s) on this task. Empty when the user isn't
  // listed as an assignee (e.g. they own the task via primary_assignee_id
  // but don't have an explicit row). Used by the planner UI to gate
  // reviewer-only actions like the "View / Complete" buttons.
  my_roles?: TaskRoleInTask[];
  readonly: true;
}

export type PlannerItem = PersonalTodo | AssignedItem;

export interface BucketResponse {
  date?: string;
  from?: string;
  to?: string;
  personal: PersonalTodo[];
  assigned: AssignedItem[];
  total: number;
}

export interface TodoSummary {
  date: string;
  total: number;
  completed: number;
  pending: number;
  overdue: number;
  completion_rate: number;
  personal: { total: number; completed: number; pending: number; overdue: number };
  assigned: { total: number; completed: number; pending: number; overdue: number };
}

export interface TodoQuickAdd {
  title: string;
  description?: string;
  notes?: string;
  due_date?: string;             // YYYY-MM-DD
  time_of_day?: string;          // HH:MM:SS
  priority?: TodoPriority;
  status?: TodoStatus;
  reminder_at?: string;
  source_task_id?: string;
  source_checklist_item_id?: string;
  is_recurring?: boolean;
  recurrence_rule?: {
    interval: "DAILY" | "WEEKLY" | "MONTHLY";
    every?: number;
    weekdays?: number[];
    until?: string;
  };
  // Optional client / vendor / project links (mirror Task create)
  client_id?: string | null;
  vendor_id?: string | null;
  project_id?: string | null;
}

export interface TodoUpdate {
  title?: string;
  description?: string;
  notes?: string;
  priority?: TodoPriority;
  status?: TodoStatus;
  due_date?: string;
  time_of_day?: string;
  reminder_at?: string;
  is_recurring?: boolean;
  recurrence_rule?: any;
  // Pass "" to clear a link; omit to leave unchanged.
  client_id?: string | null;
  vendor_id?: string | null;
  project_id?: string | null;
}

export interface TodoReminder {
  id: string;
  source: "todo" | "task";
  kind: string;
  todo_id: string | null;
  task_id: string | null;
  title: string;
  body: string | null;
  is_read: boolean;
  created_at: string;
}

const qs = (o: Record<string, any>) => {
  const p = new URLSearchParams();
  Object.entries(o).forEach(([k, v]) => v !== undefined && v !== null && p.set(k, String(v)));
  const s = p.toString();
  return s ? `?${s}` : "";
};

export const todoApi = {
  today: () => api.get<BucketResponse>(`/todo/today`),
  upcoming: (days = 30) => api.get<BucketResponse>(`/todo/upcoming?days=${days}`),
  missed: () => api.get<BucketResponse>(`/todo/missed`),
  history: (params: { from?: string; to?: string; target?: string } = {}) =>
    api.get<BucketResponse>(`/todo/history${qs(params)}`),
  summary: (target?: string) =>
    api.get<TodoSummary>(`/todo/summary${target ? `?target=${target}` : ""}`),

  // Personal CRUD
  quickAdd: (payload: TodoQuickAdd) => api.post<PersonalTodo>(`/todo/quick`, payload),
  // Multi-recipient assign — same payload as quickAdd, plus a list of
  // teammates the caller wants to push this todo onto. Backend creates one
  // Todo row per user_id with created_by = caller, so each recipient sees
  // an "Assigned by {name}" card on their planner.
  assign: (payload: TodoQuickAdd & { user_ids: string[] }) =>
    api.post<{ success: boolean; count: number; todos: PersonalTodo[] }>(`/todo/assign`, payload),
  update: (id: string, payload: TodoUpdate) => api.patch<PersonalTodo>(`/todo/${id}`, payload),
  remove: (id: string) => api.delete(`/todo/${id}`),
  toggle: (id: string, target?: TodoStatus) =>
    api.post<PersonalTodo>(`/todo/${id}/toggle`, target ? { target } : {}),
  reschedule: (id: string, due_date: string, time_of_day?: string) =>
    api.patch<PersonalTodo>(`/todo/${id}/reschedule`, { due_date, time_of_day }),
  snooze: (id: string, minutes: number) =>
    api.post<PersonalTodo>(`/todo/${id}/snooze`, { minutes }),

  // Linking + checklist import
  linkTask: (task_id: string, date?: string) =>
    api.post<{ id: string; task_id: string; date: string; existed: boolean }>(`/todo/link`, { task_id, date }),
  unlink: (link_id: string) => api.delete(`/todo/link/${link_id}`),
  fromChecklist: (payload: {
    title: string;
    source_task_id?: string;
    source_checklist_item_id?: string;
    due_date?: string;
    priority?: TodoPriority;
  }) => api.post<PersonalTodo>(`/todo/from-checklist`, payload),

  // Reminders
  reminders: (limit = 50) =>
    api.get<{ total: number; items: TodoReminder[] }>(`/todo/reminders/unread?limit=${limit}`),
  markReminderRead: (id: string, source: "todo" | "task" = "todo") =>
    api.post(`/todo/reminders/${id}/read`, { source }),
};

// Visibility (RBAC team panel)
export interface TeamMember {
  user_id: string;
  employee_id: string;
  name: string;
  email: string | null;
  role: string | null;
  designation: string | null;
  department_id: string | null;
  manager_id: string | null;
  profile_photo_url: string | null;
}

export interface UserTodayRow {
  id: string;
  source: "personal" | "assigned";
  title: string;
  status: string;
  priority: TodoPriority;
  due_date: string | null;
  completed_at: string | null;
  is_overdue?: boolean;
}
// changes in team visibiltiy
export const visibilityApi = {
  team: () => api.get<{ total: number; items: TeamMember[] }>(`/visibility/team`),
  userToday: (uid: string) =>
    api.get<{ user_id: string; date: string; personal: UserTodayRow[]; assigned: UserTodayRow[]; total: number }>(
      `/visibility/user/${uid}/today`,
    ),
  userSummary: (uid: string, target?: string) =>
    api.get<TodoSummary & { user_id: string }>(
      `/visibility/user/${uid}/summary${target ? `?target=${target}` : ""}`,
    ),
};

export default todoApi;
